import { PageLevel, useConsoleState } from "providers/ConsoleStateProvider";
import React, { useEffect } from "react";
import {
  StyledClipBoard,
  StyledMonitorButtonContainerDiv,
  StyledMonitorPage,
  StyledMonitorManagementContainer,
  StyledMonitorListWrapper,
  StyledHeadline,
  StyledSortButton,
  StyledNoDataTypography,
  StyledMinus,
  StyledCheckCircle,
  StyledSubjectNameContainer,
  StyledFixedCheckCircle,
  StyledCross,
  StyledConfirmationTextContainer,
  StyledVerticalArea,
  StyledHorizontalArea,
} from "./styled";
import { DisplayMonitor, useInterviewListForSelect, useMonitor } from "./hook";
import { usePageHeader } from "hooks/usePageHeader";
import { getDisplayMD, isInterviewStarted } from "utils/common";
import { MonitorManageModal } from "pages/MonitorManagement/MonitorManageModal";
import { PopoverWithButton } from "components/common/Popover";
import { Button } from "components/parts/Button/Button";
import { Typography } from "components/parts/Typography/Typography";
import {
  StyledNoHoverTbodyTr,
  StyledTable,
  StyledTableCell,
  StyledTbody,
  StyledTbodyTd,
  StyledThead,
  StyledTheadTh,
  StyledTheadTr,
  TableColumnProps,
} from "components/templates/Table/styled";
import { TextWithIconButton } from "components/parts/Button/TextWithIconButton";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { BackButton } from "components/parts/BackButton";
import { SelectBox } from "components/parts/Input/SelectBox";
import { EditablePlate } from "components/common/EditablePlate";
import { updateMonitorMutation } from "utils/apis/monitor";
import {
  InterviewType,
  MonitorCheckResult,
  MonitorCheckResultType,
} from "types/graphqlapi";
import { InterviewTypeIcon } from "components/parts/InterviewTypeIcon";
import { useConfirmOrAlert } from "providers/ConfirmProvider";
import { useLoadingState } from "providers/LoadingProvider";
import { updateMonitorCheckResultMutation } from "utils/apis/monitorCheckResult";
import { MonitorCheckResultDisplay } from "components/monitorManagement/MonitorCheckResultContent";

export const MonitorManagement: React.FC = () => {
  const {
    selectedSubjectId,
    setScreenName,
    setPageLevel,
    selectedSubjectType,
    isPersonalDataHandler,
  } = useConsoleState();
  const { pageHeader } = usePageHeader(selectedSubjectId);
  const {
    errorList,
    warningList,
    changeList,
    newlyList,
    modalFlag,
    modalMonitorList,
    monitorList,
    monitorCheckResultList,
    onChangePulldownHandler,
    onClickDeleteMonitorAttribute,
    importMonitor,
    onClickTSVExportHandler,
    onPasteTSVImportHandler,
    setErrorList,
    setWarningList,
    setChangeList,
    setNewlyList,
    setModalFlag,
    setModalMonitorList,
    setMonitorCheckResultList,
    refresh,
  } = useMonitor();
  const { interviewList } = useInterviewListForSelect();
  const { handleConfirm, handleAlert } = useConfirmOrAlert();
  const { setLoading } = useLoadingState();

  useEffect(() => {
    setScreenName("モニター管理");
    setPageLevel(PageLevel.fileAndScenarioAndMonitor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPasteTSVImport = (event: React.ClipboardEvent<HTMLDivElement>) => {
    onPasteTSVImportHandler(event.clipboardData.getData("text"));
  };

  const onClickTSVPaste = async () => {
    const inputString = await navigator.clipboard.readText();
    onPasteTSVImportHandler(inputString);
  };

  const onClickCloseModal = () => {
    setModalFlag(false);
    setModalMonitorList([]);
    setErrorList([]);
    setWarningList([]);
    setChangeList([]);
    setNewlyList([]);
  };

  type SortValue =
    | "No_ASC"
    | "No_DESC"
    | "assignedInterview_DESC"
    | "assignedInterview_ASC";

  const [sortItem, setSortItem] = React.useState<SortValue>("No_ASC");

  const onClickSortStartDate = () => {
    sortItem == "No_DESC" || sortItem == "No_ASC"
      ? setSortItem("assignedInterview_ASC")
      : sortItem == "assignedInterview_DESC"
      ? setSortItem("assignedInterview_ASC")
      : setSortItem("assignedInterview_DESC");
  };

  const onClickSortNo = () => {
    sortItem == "assignedInterview_DESC" || sortItem == "assignedInterview_ASC"
      ? setSortItem("No_ASC")
      : sortItem == "No_DESC"
      ? setSortItem("No_ASC")
      : setSortItem("No_DESC");
  };

  const editMemo = async (targetId: string, inputValue: string) => {
    await updateMonitorMutation({ id: targetId, memo: inputValue });
    refresh();
  };

  const onClickNotCheckedIcon = async (
    monitor: DisplayMonitor,
    monitorCheckResult: MonitorCheckResult
  ) => {
    const res = await handleConfirm({
      content: (
        <>
          <StyledConfirmationTextContainer>
            <StyledVerticalArea>
              <Typography text="属性情報の更新は完了していますか？" />
              <StyledHorizontalArea>
                <Typography text="変更済みの場合、接続テスト結果を" />
                <StyledCross />
                <Typography text="→" />
                <StyledFixedCheckCircle />
                <Typography text="に変更します。" />
              </StyledHorizontalArea>
            </StyledVerticalArea>
          </StyledConfirmationTextContainer>
          <MonitorCheckResultDisplay
            monitorCheckResult={monitorCheckResult}
            monitor={monitor}
          />
        </>
      ),
      confirmationText: "属性情報変更済み",
      dialogProps: {
        PaperProps: {
          sx: {
            maxWidth: "750px",
          },
        },
      },
    });

    if (res) {
      setLoading(true);
      try {
        const updatedMonitorCheckResult =
          await updateMonitorCheckResultMutation(
            monitorCheckResult?.id as string
          );
        const updatedMonitorCheckResultList = monitorCheckResultList.map(
          (monitorCheckResult) =>
            monitorCheckResult.id === updatedMonitorCheckResult?.id
              ? updatedMonitorCheckResult
              : monitorCheckResult
        );
        setMonitorCheckResultList(updatedMonitorCheckResultList);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
  };

  const onClickFixedIcon = async (
    monitor: DisplayMonitor,
    monitorCheckResult: MonitorCheckResult
  ) => {
    await handleAlert({
      content: (
        <MonitorCheckResultDisplay
          monitor={monitor}
          monitorCheckResult={monitorCheckResult}
        />
      ),
      confirmationText: "閉じる",
      dialogProps: {
        PaperProps: {
          sx: {
            maxWidth: "750px",
          },
        },
      },
    });
  };

  const currentMonitorList =
    sortItem === "No_ASC"
      ? [...monitorList].sort((a, b) => {
          if (a && b) {
            if (Number(a.No) < Number(b.No)) return -1;
            if (Number(a.No) > Number(b.No)) return 1;
          }
          return 0;
        })
      : sortItem === "No_DESC"
      ? [...monitorList].sort((a, b) => {
          if (a && b) {
            if (Number(a.No) < Number(b.No)) return 1;
            if (Number(a.No) > Number(b.No)) return -1;
          }
          return 0;
        })
      : sortItem === "assignedInterview_ASC"
      ? [
          ...monitorList
            .filter((monitor) => monitor?.assignedInterview?.interview_seq)
            .sort((a, b) => {
              if (a && b) {
                if (
                  !a.assignedInterview?.interview_seq ||
                  !b.assignedInterview?.interview_seq
                )
                  return 0;
                if (
                  a.assignedInterview?.interview_seq <
                  b.assignedInterview?.interview_seq
                )
                  return -1;
                if (
                  a.assignedInterview?.interview_seq >
                  b.assignedInterview?.interview_seq
                )
                  return 1;
              }
              return 0;
            }),
          ...monitorList.filter(
            (monitor) => !monitor?.assignedInterview?.interview_seq
          ),
        ]
      : sortItem === "assignedInterview_DESC"
      ? [
          ...monitorList.filter(
            (monitor) => !monitor?.assignedInterview?.interview_seq
          ),
          ...monitorList
            .filter((monitor) => monitor?.assignedInterview?.interview_seq)
            .sort((a, b) => {
              if (a && b) {
                if (
                  !a.assignedInterview?.interview_seq ||
                  !b.assignedInterview?.interview_seq
                )
                  return 0;
                if (
                  a.assignedInterview?.interview_seq <
                  b.assignedInterview?.interview_seq
                )
                  return 1;
                if (
                  a.assignedInterview?.interview_seq >
                  b.assignedInterview?.interview_seq
                )
                  return -1;
              }
              return 0;
            }),
        ]
      : [...monitorList];

  const MONITOR_TABLE_HEADER_COLUMN_LIST = [
    {
      columnId: "No",
      columnType: "typography",
      textAlign: "center",
      minWidth: "4em",
      width: "1000px",
      text: "No",
    },
    {
      columnId: "ansId",
      columnType: "typography",
      textAlign: "center",
      minWidth: "3.5em",
      width: "1000px",
      text: "管理ID",
    },
    {
      columnId: "classfication",
      columnType: "typography",
      textAlign: "center",
      minWidth: "8em",
      width: "1000px",
      text: "グループ / 割付",
    },
    {
      columnId: "name",
      columnType: "typography",
      textAlign: "left",
      minWidth: "6em",
      width: "1000px",
      text: "名前",
    },

    {
      columnId: "sex",
      columnType: "typography",
      textAlign: "center",
      minWidth: "3em",
      width: "1000px",
      text: "性別",
    },
    {
      columnId: "age",
      columnType: "typography",
      textAlign: "center",
      minWidth: "2em",
      width: "1000px",
      text: "年齢",
    },
    isPersonalDataHandler && {
      columnId: "email",
      columnType: "typography",
      textAlign: "left",
      minWidth: "15em",
      width: "1000px",
      text: "メールアドレス",
    },
    {
      columnId: "assignedInterview",
      columnType: "selectBox",
      textAlign: "left",
      minWidth: "20em",
      width: "1000px",
      text: "アサインチャットルーム",
    },
    {
      columnId: "memo",
      columnType: "typography",
      textAlign: "left",
      minWidth: "20em",
      width: "1000px",
      text: "メモ",
    },
    {
      columnId: "interviewUrl/PinCode",
      columnType: "button",
      textAlign: "center",
      minWidth: "5.5em",
      width: "1000px",
      text: "URL/PIN",
    },
    {
      columnId: "login",
      columnType: "login",
      textAlign: "center",
      minWidth: "4.5em",
      width: "1000px",
      text: "ログイン",
    },
  ] as TableColumnProps[];

  return (
    <StyledMonitorPage onPaste={onPasteTSVImport}>
      <BackButton />
      <StyledMonitorManagementContainer>
        <StyledHeadline>
          <StyledSubjectNameContainer>
            <InterviewTypeIcon interviewType={selectedSubjectType} size={24} />
            <Typography
              text={`${pageHeader.subjectName}のモニター`}
              size="xxlarge"
              weight={"bold"}
            />
          </StyledSubjectNameContainer>
          <StyledMonitorButtonContainerDiv>
            <TextWithIconButton
              onClick={onClickDeleteMonitorAttribute}
              text="追加属性一括削除"
              variant={"white"}
              iconName={TextWithIconTypes.ManageAccounts}
            />
            <Button
              onClick={onClickTSVExportHandler}
              text="コピー"
              variant={"primary"}
            >
              <StyledClipBoard />
            </Button>
            <Button
              onClick={onClickTSVPaste}
              text="ペースト"
              variant={"primary"}
            >
              <StyledClipBoard />
            </Button>
          </StyledMonitorButtonContainerDiv>
        </StyledHeadline>
        <StyledMonitorListWrapper>
          <StyledTable>
            <StyledThead>
              <StyledTheadTr>
                {MONITOR_TABLE_HEADER_COLUMN_LIST.map((column) => (
                  <StyledTheadTh
                    key={`MonitorTableHeaderColumn${column.columnId}`}
                    textAlign={column.textAlign}
                    minWidth={column.minWidth}
                    width={column.width}
                  >
                    <StyledTableCell
                      minWidth={column.minWidth}
                      style={{ margin: 0 }}
                    >
                      {column.columnId === "No" ? (
                        <StyledSortButton
                          text={
                            sortItem == "No_DESC"
                              ? "No ▼"
                              : sortItem == "No_ASC"
                              ? "No ▲"
                              : "No"
                          }
                          variant={"white"}
                          onClick={() => onClickSortNo()}
                        />
                      ) : column.columnId === "assignedInterview" ? (
                        <StyledSortButton
                          text={
                            sortItem == "assignedInterview_DESC"
                              ? "アサインチャットルーム ▼"
                              : sortItem == "assignedInterview_ASC"
                              ? "アサインチャットルーム ▲"
                              : "アサインチャットルーム"
                          }
                          variant={"white"}
                          onClick={() => onClickSortStartDate()}
                        />
                      ) : (
                        <Typography text={column.text} />
                      )}
                    </StyledTableCell>
                  </StyledTheadTh>
                ))}
              </StyledTheadTr>
            </StyledThead>
            <StyledTbody>
              {currentMonitorList.length === 0 ? (
                <tr>
                  <td>
                    <StyledNoDataTypography text="モニターがいません" />
                  </td>
                </tr>
              ) : (
                currentMonitorList.map((monitor) => {
                  const monitorCheckResult = monitorCheckResultList.find(
                    (monitorCheckResult) =>
                      monitorCheckResult.monitor_id === monitor?.id &&
                      monitorCheckResult.interview_id ===
                        monitor?.assignedInterviewId
                  );
                  return (
                    <StyledNoHoverTbodyTr key={monitor?.id}>
                      {MONITOR_TABLE_HEADER_COLUMN_LIST.map((column) => (
                        <StyledTbodyTd
                          key={`${column.columnId}Of${monitor?.id}`}
                          textAlign={column.textAlign}
                          minWidth={column.minWidth}
                          width={column.width}
                        >
                          <StyledTableCell
                            minWidth={column.minWidth}
                            style={{ margin: 0 }}
                          >
                            {column.columnType === "typography" ? (
                              column.columnId === "memo" ? (
                                <EditablePlate
                                  initValue={monitor?.memo ? monitor.memo : ""}
                                  targetMonitor={monitor as DisplayMonitor}
                                  callBack={editMemo}
                                />
                              ) : (
                                (() => {
                                  const key =
                                    column.columnId as keyof typeof monitor;
                                  const monitorContent =
                                    monitor !== undefined ? monitor[key] : "";
                                  if (typeof monitorContent === "string")
                                    return <Typography text={monitorContent} />;
                                })()
                              )
                            ) : column.columnType === "selectBox" ? (
                              isInterviewStarted(monitor?.assignedInterview) ? (
                                <Typography
                                  text={`${
                                    monitor?.assignedInterview?.interview_seq ||
                                    ""
                                  } ${
                                    monitor?.assignedInterview
                                      ?.scheduled_start_date
                                      ? getDisplayMD(
                                          new Date(
                                            monitor.assignedInterview.scheduled_start_date
                                          )
                                        )
                                      : ""
                                  } ${
                                    monitor?.assignedInterview
                                      ?.scheduled_start_time
                                  } ～ ${
                                    selectedSubjectType ===
                                      InterviewType.TERM &&
                                    monitor?.assignedInterview
                                      ?.scheduled_end_date
                                      ? getDisplayMD(
                                          new Date(
                                            monitor.assignedInterview.scheduled_end_date
                                          )
                                        )
                                      : ""
                                  } ${
                                    monitor?.assignedInterview
                                      ?.scheduled_end_time
                                  }`}
                                />
                              ) : (
                                <SelectBox
                                  name="assigned-interview"
                                  value={
                                    monitor?.assignedInterviewId
                                      ? monitor.assignedInterviewId
                                      : "0"
                                  }
                                  optionList={interviewList}
                                  onChange={(e) => {
                                    if (typeof monitor?.id === "string") {
                                      onChangePulldownHandler(
                                        e,
                                        monitor.id,
                                        monitor.assignedInterviewId
                                      );
                                    }
                                  }}
                                />
                              )
                            ) : column.columnType === "button" ? (
                              monitor?.assignedInterview && (
                                <PopoverWithButton
                                  monitor={monitor}
                                  buttonChildren={<StyledClipBoard />}
                                />
                              )
                            ) : column.columnType === "login" ? (
                              monitorCheckResult &&
                              monitorCheckResult.monitor_check_result_type ===
                                MonitorCheckResultType.CHECKED ? (
                                <StyledCheckCircle />
                              ) : monitorCheckResult &&
                                monitorCheckResult.monitor_check_result_type ===
                                  MonitorCheckResultType.NOT_CHECKED ? (
                                <StyledCross
                                  onClick={() => {
                                    monitor &&
                                      onClickNotCheckedIcon(
                                        monitor,
                                        monitorCheckResult
                                      );
                                  }}
                                />
                              ) : monitorCheckResult &&
                                monitorCheckResult.monitor_check_result_type ===
                                  MonitorCheckResultType.FIXED ? (
                                <StyledFixedCheckCircle
                                  onClick={() => {
                                    monitor &&
                                      onClickFixedIcon(
                                        monitor,
                                        monitorCheckResult
                                      );
                                  }}
                                />
                              ) : (
                                <StyledMinus />
                              )
                            ) : (
                              ""
                            )}
                          </StyledTableCell>
                        </StyledTbodyTd>
                      ))}
                    </StyledNoHoverTbodyTr>
                  );
                })
              )}
            </StyledTbody>
          </StyledTable>
        </StyledMonitorListWrapper>
        {modalFlag && (
          <MonitorManageModal
            closeHandler={onClickCloseModal}
            confirmMonitorList={modalMonitorList}
            onClickImportButton={importMonitor}
            onClickCancelButton={onClickCloseModal}
            errorList={errorList}
            warningList={warningList}
            changeList={changeList}
            newlyList={newlyList}
            importButtonDisabled={
              errorList.length > 0 ||
              (newlyList.length === 0 && changeList.length === 0)
            }
          />
        )}
      </StyledMonitorManagementContainer>
    </StyledMonitorPage>
  );
};
